import axios, { AxiosError, AxiosInstance } from "axios";
import { refresh } from "./auth";
import { readBlobText } from "@/shared/lib/download";

export const API_URL = process.env.API_URL

interface AuthErrorResponse {
    error_id?: string
}

/**
 * У выгрузок `responseType: "blob"`, поэтому тело ошибки приходит объектом Blob
 * и обычная проверка `data.error_id` его не видит. Без разбора такой ответ не
 * попал бы ни в ветку продления токена, ни в редирект, и экспорт молча падал бы
 * до тех пор, пока сессию не обновит какой-нибудь другой запрос.
 */
const readAuthErrorId = async (
    error: AxiosError<AuthErrorResponse>,
): Promise<string | undefined> => {
    const payload: unknown = error.response?.data

    if (typeof Blob !== "undefined" && payload instanceof Blob) {
        try {
            const parsed: unknown = JSON.parse(await readBlobText(payload))

            return typeof parsed === "object" && parsed !== null
                ? (parsed as AuthErrorResponse).error_id
                : undefined
        } catch {
            return undefined
        }
    }

    return error.response?.data?.error_id
}

export const handleAuthFailure = async (
    error: AxiosError<AuthErrorResponse>,
    retryRequest: (config: NonNullable<typeof error.config>) => Promise<unknown>,
) => {
    const cfg = error.config;

    if (cfg?.authFailureMode === "local") {
        return Promise.reject(error)
    }

    const errorId = await readAuthErrorId(error)

    if (errorId === 'unauthorized') {
        window.location.href = '/auth';

        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');

        return Promise.reject(error);
    }
    if (errorId === 'expired_access_token' && cfg && !cfg._retry) {
        try {
            cfg._retry = true;
            const refreshToken = localStorage.getItem('refresh_token')
            if (!refreshToken) {
                return Promise.reject(error);
            }

            const response = await refresh(refreshToken)

            localStorage.setItem('access_token', response.data.access_token);
            localStorage.setItem('refresh_token', response.data.refresh_token);

            cfg.headers.Authorization = `Bearer ${response.data.access_token}`;

            return retryRequest(cfg);
        } catch {
            window.location.href = '/auth';

            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');

            return Promise.reject(error);
        }
    }

    return Promise.reject(error);
}

class ApiInstance {
    private axios: AxiosInstance

    constructor(baseUrl: string) {
        this.axios = axios.create({
            baseURL: baseUrl
        })
        this.axios.interceptors.response.use(
            (response) => {
                return response;
            },
            (error: AxiosError<AuthErrorResponse>) => handleAuthFailure(error, (config) => this.axios(config)),
        );
        this.axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token') 
        
                if (token) {
                    config.headers["Authorization"] = `Bearer ${token}`;
                }
        
                return config;
            },
            (error) => {
                return Promise.reject(error);
            },        
        )
    }
    get handler() {
        return this.axios
    }
}

export const apiInstance = new ApiInstance(API_URL)
